import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchExchangeStats, listReturns } from '@/api/returns';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExportControls } from '@/components/ui/ExportControls';
import { KpiCard } from '@/components/ui/KpiCard';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { reasonLabel } from '@/constants/defaults';
import { lastNDaysRange } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { formatCurrency, formatDate, formatNumber } from '@/lib/format';
import { avatarGradient, returnAmount } from '@/lib/returnRow';
import { cn } from '@/lib/cn';
import type { ExchangeStats, Page, ReturnRecord } from '@/types';
import { paths } from '@/router/paths';

const PAGE_SIZE = 20;

export function ExchangesScreen() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));

  const { data: stats } = useAsyncData<ExchangeStats | null>(
    (signal) => fetchExchangeStats(range, signal),
    [range],
  );
  const { data, errorMessage, isLoading, reload } = useAsyncData<Page<ReturnRecord> | null>(
    (signal) =>
      listReturns({ isExchange: true, page, size: PAGE_SIZE, from: range.from, to: range.to }, signal),
    [page, range],
  );

  const rows = data?.content ?? [];

  return (
    <div className="animate-fade-in pb-10">
      <PageHeader
        title="Exchanges"
        description="Returns where the customer chose a replacement."
        actions={
          <>
            <DateRangePicker
              value={range}
              onChange={(nextRange) => {
                setRange(nextRange);
                setPage(0);
              }}
            />
            <ExportControls type="exchange" filenamePrefix="exchanges" range={range} />
          </>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Total exchanges" value={formatNumber(stats?.totalExchanges)} />
        <KpiCard label="Pending" value={formatNumber(stats?.pendingExchanges)} />
        <KpiCard label="Completed" value={formatNumber(stats?.completedExchanges)} />
        <KpiCard label="Exchange rate" value={`${stats?.exchangeRatePct ?? 0}%`} />
      </div>

      {isLoading ? (
        <LoadingState message="Loading exchanges…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : (
        <div className="flex flex-col gap-4">
          <Card padded={false} className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[880px]">
                {/* Header row */}
                <div className="grid grid-cols-[1.2fr_0.9fr_1.1fr_1.4fr_1fr_0.8fr_1fr] items-center gap-3 border-b border-line bg-surface-sunken/60 px-4 py-3">
                  <HeaderCell>Request</HeaderCell>
                  <HeaderCell>Created</HeaderCell>
                  <HeaderCell>Customer</HeaderCell>
                  <HeaderCell>Items</HeaderCell>
                  <HeaderCell>Reason</HeaderCell>
                  <HeaderCell>Amount</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                </div>

                {rows.length === 0 ? (
                  <p className="px-4 py-12 text-center text-sm text-ink-muted">No exchanges yet.</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {rows.map((row) => {
                      const firstItem = row.items?.[0];
                      const itemCount = row.items?.length ?? 0;
                      const amount = returnAmount(row);
                      return (
                        <li
                          key={row.returnId}
                          onClick={() => navigate(paths.returnDetail(row.returnId))}
                          className="grid cursor-pointer grid-cols-[1.2fr_0.9fr_1.1fr_1.4fr_1fr_0.8fr_1fr] items-center gap-3 px-4 py-3.5 transition-colors hover:bg-surface-sunken/40"
                        >
                          <div className="min-w-0">
                            <div className="font-mono text-sm font-semibold text-ink">{row.returnId}</div>
                            {row.shopifyOrderNumber ? (
                              <div className="truncate font-mono text-xs text-ink-subtle">
                                {row.shopifyOrderNumber}
                              </div>
                            ) : null}
                          </div>

                          <div className="truncate text-sm text-ink-muted">{formatDate(row.createdAt) || '—'}</div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-ink">
                              {row.customerName || '—'}
                            </div>
                            <div className="truncate text-xs text-ink-muted">
                              {row.customerEmail ?? '—'}
                            </div>
                          </div>

                          <div className="flex min-w-0 items-center gap-3">
                            {firstItem?.imageUrl ? (
                              <img
                                src={firstItem.imageUrl}
                                alt=""
                                className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                              />
                            ) : (
                              <span
                                className={cn(
                                  'h-10 w-10 flex-shrink-0 rounded-lg bg-gradient-to-br',
                                  avatarGradient(row.returnId),
                                )}
                              />
                            )}
                            <div className="min-w-0">
                              <div className="truncate text-sm text-ink">
                                {firstItem?.productTitle ?? '—'}
                              </div>
                              {itemCount > 1 ? (
                                <div className="truncate text-xs text-ink-subtle">+{itemCount - 1} more</div>
                              ) : null}
                            </div>
                          </div>

                          <div className="truncate text-sm text-ink-muted">{reasonLabel(row.reasonSlug)}</div>

                          <div className="text-sm font-semibold text-ink">
                            {amount != null ? formatCurrency(amount) : '—'}
                          </div>

                          <div>
                            <StatusBadge status={row.status} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </Card>

          {data && data.totalPages > 0 ? (
            <Pagination
              page={data.page ?? page}
              totalPages={data.totalPages}
              totalElements={data.totalElements}
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function HeaderCell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('text-xs font-semibold uppercase tracking-wide text-ink-subtle', className)}>
      {children}
    </span>
  );
}
